import React, { FC, ReactNode, RefObject, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { UnreachableCaseError } from '../../entities/UnreachableCaseError';
import useSesame from '../../hooks/useSesame';
import Cross from '../../svg/Cross';
import FlagMarker from '../../svg/FlagMarker';
import MapIcon from '../../svg/Map';
import HouseMarker from '../../svg/Marker';
import Button from '../action/Button';
import Compass from '../figure/Compass';
import Img from '../figure/Img';
import MapSattelite from '../figure/MapSattelite';
import ZoomIn from '../figure/ZoomIn';
import ZoomOut from '../figure/ZoomOut';
import { ModalHeaderContainer } from '../layout/ModalHeader';
import Title from '../primitive/Title';
import MapGL, { Marker, NavigationControl, Map } from 'react-map-gl';
import { WebMercatorViewport } from '@math.gl/web-mercator';

export interface Position {
    latitude: number;
    longitude: number;
}

type MarkerElement = 'flag' | 'house' | React.JSX.Element;

interface MarkerInterface {
    markerPosition: Position;
    markerElement?: MarkerElement;
}

export interface MapLoadEvent {
    type: string;
    target: unknown;
}

const getMarker = (el: MarkerElement | undefined) => {
    if (typeof el !== 'string') {
        return el;
    }
    switch (el) {
        case 'flag':
            return <FlagMarker />;
        case 'house':
            return <HouseMarker />;
        default:
            throw new UnreachableCaseError(el);
    }
};
export interface StaticMapProps {
    loading?: 'lazy' | 'eager';
    position?: Position;
    zoom: number;
    className?: string;
    width?: number;
    height?: number;
    showMapText: ReactNode;
    /** Tip: use the useResizeObserver package to specify a higher minZoom level for mobile devices. */
    minZoom: number;
    markers?: MarkerInterface[]; // use marker when adding multiple markers to a map
    markerPosition?: Position; // use marker position when only one marker needs to be added.
    iconUrl?: string; // add icon url when only one marker needs to be added
    iconUrl2x?: string;
    markerElement?: MarkerElement;
    fullWidth?: boolean;
    locale?: string;
    interactive?: boolean; // pass true to skip interactivity check and load full map directly
    mapboxApiAccessToken?: string;
}

interface MapProps {
    mapboxAccessToken?: string;
    mapStyle: string;
    latitude?: number;
    longitude?: number;
    zoom?: number;
}

type Coordinates = [number, number];

// the mapbox map style
const mapStyle = 'mapbox/outdoors-v11';

// amount of hovering time (in ms) it takes for map to become interactive
const interactiveTimeout = 500;

const MAX_WIDTH = 804;
const MAX_HEIGHT = 1091;

const getBoundsForPoints = (points: MarkerInterface[], zoom: number) => {
    const pointsLong = points.map(point => point.markerPosition.longitude);
    const pointsLat = points.map(point => point.markerPosition.latitude);
    const cornersLongLat: [Coordinates, Coordinates] = [
        [Math.min(...pointsLong), Math.min(...pointsLat)],
        [Math.max(...pointsLong), Math.max(...pointsLat)],
    ];
    const viewport = new WebMercatorViewport({ width: 800, height: 600 }).fitBounds(cornersLongLat, {
        padding: 200,
    }); // Can also use option: offset: [0, -100]
    const { longitude, latitude } = viewport;
    return {
        longitude,
        latitude,
        // don't zoom any further than the provided zoom
        zoom: viewport.zoom > zoom ? zoom : viewport.zoom,
    };
};
const positionToString = (position: Position) => `${position.longitude},${position.latitude}`;

const buildStaticUrl = (
    {
        markerPosition,
        position,
        zoom,
        iconUrl,
        iconUrl2x,
        width = MAX_WIDTH,
        height = MAX_HEIGHT,
        mapboxApiAccessToken,
    }: StaticMapProps,
    retina = false
) =>
    iconUrl2x &&
    iconUrl &&
    markerPosition &&
    position &&
    `https://mapbox.travelbase.nl/styles/v1/${mapStyle}/static/url-${encodeURIComponent(
        retina ? iconUrl2x : iconUrl
    )}(${positionToString(markerPosition)})/${positionToString(position)},${zoom}/${width}x${height}${
        retina ? '@2x' : ''
    }?access_token=${mapboxApiAccessToken}&logo=false&attribution=false`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const setLocale = (locale: string, mapRef: RefObject<any>) => {
    const map = mapRef.current?.getMap();
    if (map) {
        map.getStyle().layers.forEach((layer: { id: string }) => {
            if (layer.id.endsWith('-label')) {
                map.setLayoutProperty(layer.id, 'text-field', ['coalesce', ['get', `name_${locale}`], ['get', 'name']]);
            }
        });
    }
};

/**
 *  When using the StaticMap component you need to wrap it in a div e.g. in order to specify its dimensions.
 */

const StaticMap: FC<React.PropsWithChildren<StaticMapProps>> = props => {
    const {
        position,
        zoom,
        className,
        showMapText,
        minZoom,
        markerPosition,
        iconUrl,
        iconUrl2x,
        markers,
        fullWidth,
        loading = 'lazy',
        interactive,
        locale,
        mapboxApiAccessToken,
        markerElement = 'house',
    } = props;
    const viewportPosition = position
        ? { latitude: position.latitude, longitude: position.longitude, zoom }
        : markers && getBoundsForPoints(markers, zoom);

    const [viewport, setViewport] = useState({
        zoom,
        ...viewportPosition,
    });

    // We don't want to set the width and height explicitly in order for the map to be responsive
    // const handleViewPortChange = ({ width: vpWidth, height: vpHeight, ...rest }) => setViewport(rest);

    const [isInteractive, setIsInteractive] = useState(interactive);
    const [isLoaded, setIsLoaded] = useState(false);
    const timeoutId = useRef<number | null>(null);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any

    const { open, onClose, onOpen } = useSesame();
    const [isSatellite, setIsSatellite] = useState(false);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const mapRef = useRef<any>(null);

    useEffect(() => {
        if (locale) {
            setLocale(locale, mapRef);
        }
    }, [locale]);

    const startInteraction = () => {
        if (isInteractive) {
            return;
        }
        timeoutId.current = window.setTimeout(() => {
            setIsInteractive(true);
        }, interactiveTimeout);
    };

    const modalReady = isLoaded && open;
    const mapMinZoom = !open ? minZoom : minZoom + 1.5;

    const mapProps: MapProps = {
        ...viewport,
        mapboxAccessToken: mapboxApiAccessToken,
        mapStyle: `mapbox://styles/${isSatellite ? 'mapbox/satellite-streets-v11' : mapStyle}`,
    };

    return (
        <Container open={open} fullWidth={fullWidth} className={className} onClick={startInteraction}>
            {markerPosition && iconUrl && iconUrl2x && (
                <Img
                    layout="fill"
                    loading={loading}
                    alt="Kaart"
                    fallback={{ src: buildStaticUrl(props) }}
                    sources={[
                        {
                            srcSet: `${buildStaticUrl(props)} 1x, ${buildStaticUrl(props, true)} 2x`,
                        },
                    ]}
                />
            )}
            {markers && (
                <Wrapper>
                    <Map interactive={false} {...mapProps}>
                        {markers.map((marker: MarkerInterface, key: number) => (
                            <Marker {...marker.markerPosition} key={key}>
                                {getMarker(marker.markerElement)}
                            </Marker>
                        ))}
                    </Map>
                </Wrapper>
            )}
            {!open && (
                <MapNav open={open}>
                    <StaticControls>
                        <StaticZoomIn />
                        <StaticZoomOut />
                        <StaticCompass />
                    </StaticControls>
                    <ViewToggle>
                        <MapSattelite />
                    </ViewToggle>
                </MapNav>
            )}

            {isInteractive && (
                <MapGLContainer open={open} style={{ opacity: +isLoaded }}>
                    <MapGL
                        initialViewState={viewport}
                        onLoad={() => {
                            if (locale) {
                                setLocale(locale, mapRef);
                            }
                            setIsLoaded(true);
                        }}
                        minZoom={mapMinZoom}
                        ref={mapRef}
                        onMove={s => {
                            setViewport(s.viewState);
                        }}
                        {...mapProps}
                    >
                        {markerPosition && <Marker {...markerPosition}>{getMarker(markerElement)}</Marker>}
                        {markers?.map((marker: MarkerInterface, key: number) => (
                            <Marker {...marker.markerPosition} key={key}>
                                {getMarker(marker.markerElement ?? markerElement)}
                            </Marker>
                        ))}
                        <MapNav open={isInteractive}>
                            <NavigationControl position="top-left" />
                            <ViewToggle onClick={() => setIsSatellite(!isSatellite)}>
                                <MapSattelite />
                            </ViewToggle>
                        </MapNav>
                    </MapGL>
                </MapGLContainer>
            )}

            {modalReady ? (
                <StyledModalHeader>
                    <StyledTitle>Locatie</StyledTitle>
                    <CloseButton onClick={onClose} aria-label="Close Modal">
                        <StyledCross />
                    </CloseButton>
                </StyledModalHeader>
            ) : (
                <ShowMapButton
                    onClick={() => {
                        startInteraction();
                        onOpen();
                    }}
                    // Show a spinner while loading the map
                    submitting={!isLoaded && open}
                >
                    <MapIcon />
                    <span>{showMapText}</span>
                </ShowMapButton>
            )}
        </Container>
    );
};

const MapNav = styled.div<{ open: boolean }>`
    position: absolute;
    top: ${({ open }) => (open ? '10.5rem' : '10px')};
    left: 10px;

    > * + * {
        margin-top: 0.4rem;
    }
`;

const ViewToggle = styled.button`
    display: flex;
    background: #fff;
    border-radius: 4px;
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.1);
    width: 28px;
    height: 28px;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    color: rgba(0, 0, 0, 0.847);
    justify-content: center;
    align-items: center;
`;

const StaticControls = styled(ViewToggle)`
    flex-direction: column;
    height: 87px;
    cursor: auto;
    outline: none;

    :focus {
        outline: none;
    }
`;

const StaticZoomIn = styled(ZoomIn)`
    height: 29px;
    width: 29px;
`;

const StaticZoomOut = styled(ZoomOut)`
    height: 29px;
    width: 29px;
    border-top: 1px solid #ddd;
`;

const StaticCompass = styled(Compass)`
    height: 29px;
    width: 29px;
    border-top: 1px solid #ddd;
`;

const CloseButton = styled.button`
    --padding: 1rem;
    position: absolute;
    padding: var(--padding);
    right: calc(2.4rem - var(--padding));
    color: currentColor;
`;

const StyledModalHeader = styled(ModalHeaderContainer)`
    align-items: center;
    position: fixed;
    z-index: 1;
    width: 100vw;
    border-color: ${({ theme }) => theme.colors.primary['80']};
    color: ${({ theme }) => theme.colors.neutral['0']};
    left: 0;
    top: 0;
`;

const ShowMapButton = styled(Button).attrs({ variant: 'outline' })`
    position: absolute;
    bottom: ${({ theme }) => theme.spacing['50_Semi']};

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.s}) {
        display: none;
    }
`;

const MapGLContainer = styled.div<{ open: boolean }>`
    position: absolute;
    transition: opacity 0.1s;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    height: 100%;

    ${({ open }) =>
        open &&
        css`
            width: 100vw;
            height: 100vh;
            position: fixed;
        `}
`;

const Container = styled.div<{ open: boolean; fullWidth?: boolean }>`
    ${({ open, theme }) =>
        open &&
        css`
            z-index: ${theme.zIndices.modal};
        `};
    display: flex;
    justify-content: center;
    position: relative;
    width: 100%;
    height: 100%;
    ${({ fullWidth }) =>
        !fullWidth &&
        `
        max-width: ${MAX_WIDTH / 10}rem;
        max-height: ${MAX_HEIGHT / 10}rem;
    `}

    > img {
        width: 100%;
        height: 100%;
        object-fit: none;
    }
`;

const Wrapper = styled.div`
    width: 100%;
    height: 100%;
`;

const StyledTitle = styled(Title)`
    color: ${({ theme }) => theme.colors.primary['80']};
`;

const StyledCross = styled(Cross)`
    color: ${({ theme }) => theme.colors.primary['80']};
`;

export default StaticMap;
